export interface DeviceCapabilities {
  webgl2: boolean;
  webgpu: boolean;
  maxTextureSize: number;
  maxTextureUnits: number;
  maxVertexUniforms: number;
  floatTextures: boolean;
  halfFloatTextures: boolean;
  floatTextureFilter: boolean;
  depthTexture: boolean;
  /** Available compressed texture formats, by extension name. */
  compressedFormats: string[];
  anisotropy: number;
  devicePixelRatio: number;
  /** navigator.deviceMemory in GB, or null if unavailable. */
  deviceMemory: number | null;
  hardwareConcurrency: number;
  highPrecisionFragment: boolean;
  reducedMotion: boolean;
  renderer: string;
  vendor: string;
  /** True when the GPU string suggests software rasterisation. */
  softwareRenderer: boolean;
}

/**
 * Probes what the browser and GPU can actually do, before any expensive
 * subsystem is enabled.
 *
 * Everything here is read once at startup from a throwaway context. Nothing in
 * the game branches on `navigator.userAgent`: capability detection is by
 * feature, so an unusual device gets a sensible profile rather than a guess.
 */
export function detectCapabilities(): DeviceCapabilities {
  const canvas = document.createElement('canvas');
  let gl: WebGL2RenderingContext | null = null;
  try {
    gl = canvas.getContext('webgl2', { failIfMajorPerformanceCaveat: false }) as WebGL2RenderingContext | null;
  } catch {
    gl = null;
  }

  const webgpu = typeof navigator !== 'undefined' && 'gpu' in navigator;
  const reducedMotion =
    typeof matchMedia === 'function' ? matchMedia('(prefers-reduced-motion: reduce)').matches : false;

  if (!gl) {
    return {
      webgl2: false,
      webgpu,
      maxTextureSize: 2048,
      maxTextureUnits: 8,
      maxVertexUniforms: 128,
      floatTextures: false,
      halfFloatTextures: false,
      floatTextureFilter: false,
      depthTexture: false,
      compressedFormats: [],
      anisotropy: 1,
      devicePixelRatio: typeof devicePixelRatio === 'number' ? devicePixelRatio : 1,
      deviceMemory: readDeviceMemory(),
      hardwareConcurrency: typeof navigator !== 'undefined' ? navigator.hardwareConcurrency || 2 : 2,
      highPrecisionFragment: false,
      reducedMotion,
      renderer: 'unknown',
      vendor: 'unknown',
      softwareRenderer: true,
    };
  }

  const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
  const renderer = debugInfo ? String(gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)) : 'unknown';
  const vendor = debugInfo ? String(gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL)) : 'unknown';

  const anisoExt =
    gl.getExtension('EXT_texture_filter_anisotropic') ??
    gl.getExtension('WEBKIT_EXT_texture_filter_anisotropic');

  const compressed: string[] = [];
  for (const name of [
    'WEBGL_compressed_texture_s3tc',
    'WEBGL_compressed_texture_etc',
    'WEBGL_compressed_texture_astc',
    'WEBGL_compressed_texture_pvrtc',
    'EXT_texture_compression_bptc',
  ]) {
    if (gl.getExtension(name)) compressed.push(name);
  }

  const precision = gl.getShaderPrecisionFormat(gl.FRAGMENT_SHADER, gl.HIGH_FLOAT);

  const caps: DeviceCapabilities = {
    webgl2: true,
    webgpu,
    maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE) as number,
    maxTextureUnits: gl.getParameter(gl.MAX_TEXTURE_IMAGE_UNITS) as number,
    maxVertexUniforms: gl.getParameter(gl.MAX_VERTEX_UNIFORM_VECTORS) as number,
    floatTextures: gl.getExtension('EXT_color_buffer_float') !== null,
    halfFloatTextures: gl.getExtension('EXT_color_buffer_half_float') !== null,
    floatTextureFilter: gl.getExtension('OES_texture_float_linear') !== null,
    depthTexture: true, // Core in WebGL2.
    compressedFormats: compressed,
    anisotropy: anisoExt ? (gl.getParameter(anisoExt.MAX_TEXTURE_MAX_ANISOTROPY_EXT) as number) : 1,
    devicePixelRatio: typeof devicePixelRatio === 'number' ? devicePixelRatio : 1,
    deviceMemory: readDeviceMemory(),
    hardwareConcurrency: navigator.hardwareConcurrency || 4,
    highPrecisionFragment: precision !== null && precision.precision > 0,
    reducedMotion,
    renderer,
    vendor,
    softwareRenderer: /swiftshader|llvmpipe|software|basic render/i.test(renderer),
  };

  // Release the probe context promptly; browsers cap simultaneous contexts.
  const lose = gl.getExtension('WEBGL_lose_context');
  lose?.loseContext();

  return caps;
}

function readDeviceMemory(): number | null {
  if (typeof navigator === 'undefined') return null;
  const mem = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
  return typeof mem === 'number' ? mem : null;
}

/** How many terrain workers to start, given the machine and the profile. */
export function recommendedWorkerCount(caps: DeviceCapabilities): number {
  const cores = caps.hardwareConcurrency;
  // Leave the main thread and one spare core alone; terrain generation should
  // never be the reason the render loop misses frames.
  return Math.max(1, Math.min(6, cores - 2));
}
